// A TASK'S LIST ON PAPER — printed, or saved as PDF through the browser's own print
// dialog. The browser lays the Hebrew out right to left exactly as on screen, which no PDF
// library here would, and the server does nothing new.
//
// IT PRINTS WHAT THE PANEL SHOWED: the list's saved grouping and order (079), and the
// filters it was opened with (group, state, search — in the address, printUrl). Whenever
// anything is filtered the sheet says so in one line, so a part of a list is never taken
// for the whole of it.
//
// Above the sheet, never printed: which columns go on paper (a phone column starts
// unticked — a guest list is handed to people), the note, and an empty ✓ column for
// ticking names off at the door. The choice is remembered per list in this browser.
//
// The sheet is always paper-white (the --paper tokens are the same in both themes).
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import he from '../../i18n/he';
import useAsync from '../../hooks/useAsync';
import { taskListsApi } from '../../api';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import DateText, { formatDate } from '../../components/DateText';
import { visibleItems } from '../../components/lists/listView';
import { groupRows, groupingOf, groupChoices } from '../../components/lists/listGroups';
import { readPrintFilters } from '../../components/lists/printUrl';
import { todayStr } from '../../utils/dates';
import PrintOptions, { usePrintChoice } from './PrintOptions';

const L = he.lists;

const cellText = (column, value) => (column.kind === 'date' && value ? formatDate(value) : value || '');

function Sheet({ data, filters: asked, choice }) {
  const { task, list } = data;
  const grouping = groupingOf(list);
  // A group the list no longer has (its grouping changed since the address was made) is
  // ignored, never printed as an empty filter: the sheet shows the whole list instead.
  const group = asked.groupKey ? groupChoices(list).find((g) => g.key === asked.groupKey) : null;
  const filters = { ...asked, groupKey: group ? group.key : null };
  const items = visibleItems(list.items, { ...filters, grouping });
  const groups = groupRows(items, list);
  const states = new Map(list.states.map((s) => [s.id, s]));
  // The grouping column is the group's heading already; printed again on every row it
  // would say the same thing twice.
  const columns = list.columns.filter((c) => choice.columns.includes(c.id) && c.id !== grouping.column?.id);
  const width = 2 + columns.length + (choice.note ? 1 : 0) + (choice.check ? 1 : 0);

  const filterParts = [
    group && `${L.printGroup}: ${group.label}`,
    filters.stateId && `${L.state}: ${states.get(filters.stateId)?.label ?? ''}`,
    filters.query && `${L.printSearch}: ${filters.query}`,
  ].filter(Boolean);
  const totals = list.states
    .map((s) => [s, items.filter((it) => it.state_id === s.id).length])
    .filter(([, n]) => n > 0);

  return (
    <article className="print-sheet">
      <header className="print-head">
        <h1>{task.title}</h1>
        <p className="print-meta">
          {[task.client_id == null ? he.tasks.internal : task.client_name, task.job_title,
            task.assigned_partner_name && `${he.board.assigneeLabel}: ${task.assigned_partner_name}`]
            .filter(Boolean).join(' · ')}
          {' · '}{L.printed} <DateText value={todayStr()} />
        </p>
        {filterParts.length > 0 && (
          <p className="print-filter">{[...filterParts, L.printShowing(items.length, list.items.length)].join(' · ')}</p>
        )}
        <p className="print-totals">{totals.map(([s, n]) => `${s.label} ${n}`).join(' · ')}</p>
      </header>

      <table className="print-table">
        <thead>
          <tr>
            <th className="print-num">#</th>
            <th>{L.name}</th>
            <th>{L.state}</th>
            {columns.map((c) => <th key={c.id}>{c.label}</th>)}
            {choice.note && <th>{L.note}</th>}
            {choice.check && <th className="print-check">✓</th>}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.key}>
            {g.label != null && (
              <tr className="print-group">
                <td colSpan={width + 1}>{g.label}<span>{L.groupMeta(g.items.length, g.finished)}</span></td>
              </tr>
            )}
            {g.items.map((it, i) => {
              const state = states.get(it.state_id);
              return (
                <tr key={it.id}>
                  <td className="print-num">{i + 1}</td>
                  <td className="print-name">{it.name}</td>
                  <td><span className={`print-state${state?.is_finished ? ' fin' : ''}`}>{state?.label}</span></td>
                  {columns.map((c) => (
                    <td key={c.id} className={c.kind === 'phone' || c.kind === 'email' ? 'print-ltr' : undefined}>
                      {cellText(c, it.cells[c.id])}
                    </td>
                  ))}
                  {choice.note && <td className="print-note">{it.note}</td>}
                  {choice.check && <td className="print-check"><span className="print-box" /></td>}
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
      {items.length === 0 && <p className="print-meta">{L.noMatch}</p>}
    </article>
  );
}

export default function ListPrint() {
  const { taskId } = useParams();
  const location = useLocation();
  const filters = readPrintFilters(location.search);
  const { data, loading, error } = useAsync(() => taskListsApi.get(taskId), [taskId]);
  const [choice, setChoice] = usePrintChoice(data?.list);
  const [title] = useState(() => document.title);

  // The PDF is saved under the page's title: the list's name and the day it was printed.
  useEffect(() => {
    if (data) document.title = `${data.task.title} — ${formatDate(todayStr())}`;
    return () => { document.title = title; };
  }, [data, title]);

  return (
    <div className="print-page" dir="rtl">
      <AsyncBoundary loading={loading} error={error}>
        {data && choice && (
          <>
            <PrintOptions list={data.list} choice={choice} onChoice={setChoice}>
              <Button variant="subtle" small onClick={() => window.close()}>{L.printBack}</Button>
              <Button small onClick={() => window.print()}>{L.printGo}</Button>
            </PrintOptions>
            <Sheet data={data} filters={filters} choice={choice} />
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
